import { useState, useEffect } from 'react'
import type { ProyectoCorreo, MensajeCorreo } from '@/lib/types'
import {
  IconRefresh, IconTrash, IconExternalLink, IconPaperclip, IconArrowLeft,
} from '@/components/icons/Icons'
import {
  nombreDe, inicialDe, colorAvatar, fechaLarga, fechaCorta, partirCita, primeraLinea,
} from '@/lib/correo-utils'
import ResponderCorreo from './ResponderCorreo'

/*
  Panel de lectura de un hilo. Los mensajes van colapsados con el último
  abierto, como Gmail: en un hilo largo lo que se quiere ver primero es lo
  último que pasó, no el principio.
*/
interface Props {
  basePath: string
  /** Cómo se llama la ficha en los textos: «proyecto», «oportunidad». */
  nombreFicha: string
  resumen: ProyectoCorreo
  detalle: ProyectoCorreo | null
  ocupado: boolean
  onActualizar: () => void
  onQuitar: () => void
  onVolver: () => void
  onRespondido: () => void
}

export default function CorreoHilo({
  basePath, nombreFicha, resumen, detalle, ocupado, onActualizar, onQuitar, onVolver, onRespondido,
}: Props) {
  const mensajes = detalle?.mensajes || []
  const [abiertos, setAbiertos] = useState<Set<number>>(new Set())

  // Al cambiar de hilo se abre solo el último mensaje. El efecto depende del
  // id y de la cantidad: así, al traer mensajes nuevos con "Actualizar",
  // también se abre el que acaba de llegar.
  useEffect(() => {
    setAbiertos(mensajes.length ? new Set([mensajes.length - 1]) : new Set())
  }, [resumen.id, mensajes.length])

  function alternar(i: number) {
    setAbiertos(prev => {
      const n = new Set(prev)
      if (n.has(i)) n.delete(i); else n.add(i)
      return n
    })
  }

  return (
    <div className="correos-hilo">
      <div className="correo-hilo__header">
        <button className="btn btn-sm btn-secondary correos-volver" onClick={onVolver} title="Volver a la lista">
          <IconArrowLeft size={13} />
        </button>
        <div style={{ minWidth: 0 }}>
          <div className="correo-hilo__titulo">{resumen.asunto || '(sin asunto)'}</div>
          <div className="correo-hilo__sub">
            {resumen.cantidad} {resumen.cantidad === 1 ? 'mensaje' : 'mensajes'}
            {resumen.participantes?.length > 0 && <> · {resumen.participantes.join(', ')}</>}
          </div>
        </div>
        <div className="correo-hilo__acciones">
          <a
            className="btn btn-sm btn-secondary"
            href={`https://mail.google.com/mail/u/0/#inbox/${resumen.gmail_thread_id}`}
            target="_blank"
            rel="noreferrer"
            title="Abrir el original en Gmail"
          >
            <IconExternalLink size={13} />
          </a>
          <button
            className="btn btn-sm btn-secondary"
            onClick={onActualizar}
            disabled={ocupado}
            title="Traer los mensajes nuevos del hilo"
          >
            <IconRefresh size={13} />
          </button>
          <button
            className="btn btn-sm btn-secondary"
            onClick={onQuitar}
            disabled={ocupado}
            title={`Quitar de esta ${nombreFicha} (no borra el correo de Gmail)`}
          >
            <IconTrash size={13} />
          </button>
        </div>
      </div>

      <div className="correo-hilo__cuerpo">
        {!detalle ? (
          <p className="text-muted">Abriendo el hilo…</p>
        ) : (
          <>
            {mensajes.map((m, i) => (
              <Mensaje key={i} m={m} abierto={abiertos.has(i)} onToggle={() => alternar(i)} />
            ))}

            {(detalle.adjuntos?.length ?? 0) > 0 && (
              <div className="correo-msg__adjuntos" style={{ paddingLeft: 0 }}>
                {detalle.adjuntos.map((a, i) => (
                  <span key={i} className="correo-adjunto" title="Se descarga desde Gmail">
                    <IconPaperclip size={11} /> {a.nombre}
                  </span>
                ))}
              </div>
            )}

            <ResponderCorreo basePath={basePath} correo={detalle} onEnviado={onRespondido} />
          </>
        )}
      </div>
    </div>
  )
}

function Mensaje({ m, abierto, onToggle }: { m: MensajeCorreo; abierto: boolean; onToggle: () => void }) {
  const { texto, cita } = partirCita(m.cuerpo)
  const [verCita, setVerCita] = useState(false)

  return (
    <div className="correo-msg">
      <div className="correo-msg__head" onClick={onToggle}>
        <div
          className="correo-item__avatar"
          style={{ background: colorAvatar(m.de), width: 32, height: 32 }}
          aria-hidden
        >
          {inicialDe(m.de)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="correo-msg__de">{nombreDe(m.de)}</div>
          {abierto
            ? <div className="correo-msg__para">para {m.para || '(sin destinatario)'}</div>
            : <div className="correo-msg__resumen">{primeraLinea(m.cuerpo) || '(sin texto)'}</div>}
        </div>
        <span className="correo-msg__fecha" title={fechaLarga(m.fecha)}>
          {abierto ? fechaLarga(m.fecha) : fechaCorta(m.fecha)}
        </span>
      </div>

      {abierto && (
        <>
          <div className="correo-msg__texto">
            {texto || '(sin texto)'}
            {cita && (
              <>
                <button
                  className="correo-cita__btn"
                  onClick={() => setVerCita(v => !v)}
                  title={verCita ? 'Ocultar la conversación citada' : 'Ver la conversación citada'}
                >
                  ···
                </button>
                {verCita && <div className="correo-cita__texto">{cita}</div>}
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}
